import { resolveEnv } from '@/config/environments';
import type { BffMenuResponse } from '@/types/bff';

/**
 * Translation function used for fallback labels. Optional so callers without
 * i18n can still get English text.
 */
type TranslateFn = (key: string) => string;

/** English fallback labels used when no `t` is provided. */
const EN_FALLBACK_LABELS: Record<string, string> = {
  'fallback.signInPrompt': 'Manage your products and services in MyVaisala.',
  'fallback.logIn': 'Log in',
  'fallback.createId': 'Create an account',
};

/** English default lookup used when no `t` is supplied. */
const enTranslate: TranslateFn = (key) => EN_FALLBACK_LABELS[key] ?? key;

/**
 * Login/register fallback menu, shown while the BFF call is in flight or after
 * it fails. Labels use `t` so they match the selected language; built per call
 * (not cached) so a locale switch stays correct.
 *
 * @param t - translation function (from `useI18n`). Optional; English defaults
 *   (`EN_FALLBACK_LABELS`) are used when omitted.
 */
export function getFallbackMenuResponse(t?: TranslateFn): BffMenuResponse {
  const translate: TranslateFn = t ?? enTranslate;
  const config = resolveEnv();

  return {
    isAuthenticated: false,
    user: null,
    menuItems: [
      {
        label: translate('fallback.signInPrompt'),
        baseUrl: '',
        path: '',
        target: '',
        type: 'text',
      },
      {
        label: translate('fallback.logIn'),
        baseUrl: config.loginApiUrl,
        path: '',
        target: '_self',
        type: 'button',
        buttonVariant: 'primary',
        align: 'center',
        action: 'login',
        startIcon: '',
        endIcon: 'ArrowRight',
      },
      {
        label: translate('fallback.createId'),
        baseUrl: config.registerApiUrl,
        path: '/register',
        target: '_self',
        type: 'link',
        align: 'center',
      },
    ],
    appSwitcher: [],
    cartCount: 0,
    viewCart: config.cartUrl,
    emptyCart: '',
    cart: {
      products: [],
      totalProductCount: 0,
      currency: 'USD',
      summary: {
        subtotal: null,
        shipping: null,
        tax: null,
        total: 0,
        discountedSubtotal: null,
        discountedTotal: null,
        estimatedTime: null,
      },
    },
  };
}
